/**
 * PublicIncomeCard — the deterministic "is my income real?" card.
 *
 * Renders the output of POST /api/public/coach/income-split (the same engine as
 * the in-app income split — NO LLM, always exact). Two parts:
 *   1. Premium reality (the ad's real-vs-phantom bar): of the premium collected,
 *      how much has decayed into locked income vs. is still time value at risk.
 *   2. The full P&L spine (matches the in-app position card): Total → Stock leg /
 *      Option leg → Intrinsic / Extrinsic → (theta vs IV move) — so "real income"
 *      reads as its actual components, not one flat number. Plus greeks + KPIs.
 * The plain-English verdict lives in the chat, not here (chat = meaning, card = math).
 */
import React from 'react';
import MiniStockChart from '../screener/MiniStockChart';

export interface IncomeGreeks {
  delta: number | null;
  gamma: number | null;
  theta_daily: number | null;
  iv: number | null;        // decimal (0.37 = 37%)
  iv_rank: number | null;   // 0–100
}

export interface IncomeData {
  symbol: string;
  position_type: string;
  current_price: number | null;
  strike: number;
  expiry: string;
  used_expiry: string;
  days_to_expiry: number | null;
  is_itm: boolean | null;
  entry_premium_per_share: number;
  entry_premium_estimated: boolean;
  // Premium bar
  premium_if_held_to_expiry: number;   // headline credit
  real_income_earned_so_far: number;   // decayed = locked
  current_option_value: number;        // full buy-back cost now
  // P&L spine
  total_pnl: number | null;
  stock_leg: number | null;
  option_leg: number;
  intrinsic_pnl: number;
  extrinsic_pnl: number;
  extrinsic_theta: number | null;      // est.
  extrinsic_iv: number | null;         // est.
  // Benchmark
  holding_uncovered_pnl: number | null;
  call_vs_holding: number | null;
  // Greeks + KPIs
  greeks: IncomeGreeks;
  premium_harvested_pct: number | null;
  cost_basis: number | null;
  cost_basis_pct: number | null;
  price_history?: { results: Array<{ close: number; timestamp: number }> } | null;
  notes: string[];
}

export interface IncomeError {
  error: string;
  notes: string[];
}

const money = (n: number): string => {
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString()}`;
};
const signed = (n: number): string => (n > 0 ? `+${money(n)}` : money(n));
const cls = (n: number): string => (n > 0 ? 'cch-inc-pos' : n < 0 ? 'cch-inc-neg' : '');

const PublicIncomeCard: React.FC<{ data: IncomeData }> = ({ data }) => {
  const isCsp = data.position_type === 'cash_secured_put';
  const headline = data.premium_if_held_to_expiry;
  const locked = data.real_income_earned_so_far;
  const atRisk = Math.max(0, headline - locked);
  const buyback = data.current_option_value;
  const isItm = buyback > atRisk + 0.5;
  const lockedPct = headline > 0 ? Math.max(0, Math.min(100, (locked / headline) * 100)) : 0;

  const haveStock = data.stock_leg !== null && data.total_pnl !== null;
  const haveSplit = data.extrinsic_theta !== null && data.extrinsic_iv !== null;
  const haveBenchmark = data.holding_uncovered_pnl !== null && data.call_vs_holding !== null;
  const vs = data.call_vs_holding;
  const vsPhrase =
    vs == null ? '' :
    vs > 0 ? `The call beat just holding by ${signed(vs)}.` :
    vs < 0 ? `The call cost you ${money(Math.abs(vs))} vs. just holding — you capped a run.` :
    'About even with just holding the shares.';

  const dteStr = data.days_to_expiry != null ? `${data.days_to_expiry}d` : '';
  const priceStr = data.current_price != null ? `stock $${data.current_price.toFixed(2)}` : '';
  const subParts = [data.used_expiry, dteStr, priceStr].filter(Boolean);

  const g = data.greeks;
  const stats: { label: string; value: string; tone?: string }[] = [];
  if (g.delta != null) stats.push({ label: 'Delta', value: g.delta.toFixed(2) });
  if (g.theta_daily != null) stats.push({ label: 'Theta', value: `${money(g.theta_daily)}/d`, tone: 'cch-inc-pos' });
  if (g.gamma != null) stats.push({ label: 'Gamma', value: g.gamma.toFixed(3) });
  if (g.iv_rank != null) stats.push({ label: 'IV rank', value: g.iv_rank.toFixed(0) });
  if (data.premium_harvested_pct != null) stats.push({ label: 'Premium harvested', value: `${data.premium_harvested_pct.toFixed(0)}%` });
  if (data.days_to_expiry != null) stats.push({ label: 'Days to expiry', value: `${data.days_to_expiry}d` });
  if (data.cost_basis != null) {
    stats.push({
      label: 'Cost basis',
      value: `$${data.cost_basis.toFixed(2)}${data.cost_basis_pct != null ? ` (${data.cost_basis_pct > 0 ? '+' : ''}${data.cost_basis_pct.toFixed(0)}%)` : ''}`,
      tone: data.cost_basis_pct != null ? cls(data.cost_basis_pct) : undefined,
    });
  }

  return (
    <div className="cch-inc">
      <div className="cch-inc-head">
        <span className="cch-inc-sym">{data.symbol}</span>
        <span className="cch-inc-strike">${data.strike % 1 === 0 ? data.strike.toFixed(0) : data.strike}</span>
        <span className="cch-inc-type">{isCsp ? 'PUT' : 'CALL'}</span>
        {subParts.length > 0 && <div className="cch-inc-sub">{subParts.join(' · ')}</div>}
      </div>

      <div className="cch-inc-cols">
        {/* LEFT — premium reality + the full P&L spine */}
        <div className="cch-inc-col">
          {/* Premium reality — the ad's real-vs-phantom bar */}
          <div className="cch-inc-block">
            <div className="cch-inc-row cch-inc-row--head">
              <span>Premium you collected</span>
              <span className="cch-inc-num">{money(headline)}</span>
            </div>
            <div className="cch-inc-bar" role="img" aria-label={`${money(locked)} locked of ${money(headline)}`}>
              <div className="cch-inc-bar-real" style={{ width: `${lockedPct}%` }} />
            </div>
            <div className="cch-inc-legend">
              <span className="cch-inc-leg"><i className="cch-inc-dot cch-inc-dot--real" />Locked income <b>{money(locked)}</b></span>
              <span className="cch-inc-leg"><i className="cch-inc-dot cch-inc-dot--play" />Still at risk <b>{money(atRisk)}</b></span>
            </div>
            {locked > 0 && (
              <p className="cch-inc-roc">✓ Real income — <b>time-value you harvested</b>, not return of capital.</p>
            )}
            {isItm && (
              <p className="cch-inc-note">Buy it back now: <b>{money(buyback)}</b> — it’s gone in-the-money, so closing costs more than the {money(headline)} you collected.</p>
            )}
          </div>

          {/* The full P&L spine — income as its real components */}
          <div className="cch-inc-block">
            <p className="cch-inc-block-label">The full P&amp;L</p>
            {haveStock && (
              <div className="cch-inc-row cch-inc-row--total">
                <span>Total P&amp;L</span>
                <span className={`cch-inc-num ${cls(data.total_pnl!)}`}>{signed(data.total_pnl!)}</span>
              </div>
            )}
            {haveStock && (
              <div className="cch-inc-row">
                <span>{isCsp ? 'Cash-secured leg' : 'Your shares'}</span>
                <span className={`cch-inc-num ${cls(data.stock_leg!)}`}>{signed(data.stock_leg!)}</span>
              </div>
            )}
            <div className="cch-inc-row">
              <span>Option leg</span>
              <span className={`cch-inc-num ${cls(data.option_leg)}`}>{signed(data.option_leg)}</span>
            </div>
            <div className="cch-inc-row cch-inc-row--sub">
              <span>Intrinsic</span>
              <span className={`cch-inc-num ${cls(data.intrinsic_pnl)}`}>{signed(data.intrinsic_pnl)}</span>
            </div>
            <div className="cch-inc-row cch-inc-row--sub">
              <span>Extrinsic (your income)</span>
              <span className={`cch-inc-num ${cls(data.extrinsic_pnl)}`}>{signed(data.extrinsic_pnl)}</span>
            </div>
            {haveSplit && (
              <>
                <div className="cch-inc-row cch-inc-row--subsub">
                  <span>theta — time decay <span className="cch-inc-est">est</span></span>
                  <span className={`cch-inc-num ${cls(data.extrinsic_theta!)}`}>{signed(data.extrinsic_theta!)}</span>
                </div>
                <div className="cch-inc-row cch-inc-row--subsub">
                  <span>IV move <span className="cch-inc-est">est</span></span>
                  <span className={`cch-inc-num ${cls(data.extrinsic_iv!)}`}>{signed(data.extrinsic_iv!)}</span>
                </div>
              </>
            )}
            {haveBenchmark && (
              <>
                <div className="cch-inc-row cch-inc-row--bench">
                  <span>vs. just holding the shares</span>
                  <span className={`cch-inc-num ${cls(data.holding_uncovered_pnl!)}`}>{signed(data.holding_uncovered_pnl!)}</span>
                </div>
                <p className={`cch-inc-scoreline ${vs! >= 0 ? 'cch-inc-scoreline--win' : 'cch-inc-scoreline--lose'}`}>{vsPhrase}</p>
              </>
            )}
          </div>
        </div>

        {/* RIGHT — price chart + greeks/KPIs */}
        <div className="cch-inc-col">
          {data.price_history?.results?.length ? (
            <div className="cch-inc-chart">
              <MiniStockChart
                key={`${data.symbol}-${data.strike}-${data.used_expiry}`}
                symbol={data.symbol}
                chartData={data.price_history}
                strikePrice={data.strike}
                entryPrice={data.cost_basis ?? undefined}
                entryPremium={data.entry_premium_per_share}
                positionType={data.position_type}
                realtimePrice={data.current_price ?? undefined}
                expiryDate={data.used_expiry}
                height={150}
                showVolume={false}
                showLegend={false}
              />
            </div>
          ) : null}

          {stats.length > 0 && (
            <div className="cch-inc-block">
              <div className="cch-inc-stats">
                {stats.map((s) => (
                  <div key={s.label} className="cch-inc-stat">
                    <span className="cch-inc-stat-label">{s.label}</span>
                    <span className={`cch-inc-stat-val ${s.tone || ''}`}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {data.entry_premium_estimated && (
        <p className="cch-inc-fine">Entry premium estimated from the option’s value on your open date.</p>
      )}
      {data.used_expiry !== data.expiry && (
        <p className="cch-inc-fine">No contract listed for {data.expiry}; used the nearest listed expiry {data.used_expiry}.</p>
      )}
    </div>
  );
};

export default PublicIncomeCard;
